import type { Room } from 'livekit-client';

/** LiveAvatar FULL — commands on LiveKit topic `agent-control`. */
export const LIVEAVATAR_CONTROL_TOPIC = 'agent-control';

/** Responses (transcription, speak_started, etc.) use topic `agent-response`. */
export const LIVEAVATAR_RESPONSE_TOPIC = 'agent-response';

export type LiveAvatarControlEvent =
  | 'avatar.speak_text'
  | 'avatar.speak_response'
  | 'avatar.interrupt'
  | 'avatar.start_listening'
  | 'avatar.stop_listening';

export function newLiveAvatarEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `la-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Wire format matches @heygen/liveavatar-web-sdk LiveAvatarSession.repeat() /
 * publishAgentControl — top-level `event_type` + `text`, not nested `payload`.
 * @see https://docs.liveavatar.com/docs/full-mode/events
 */
export function buildLiveAvatarControlPayload(
  eventType: LiveAvatarControlEvent,
  fields: Record<string, unknown> = {},
) {
  if (eventType === 'avatar.interrupt') {
    return { event_type: eventType };
  }
  if (eventType === 'avatar.speak_text' || eventType === 'avatar.speak_response') {
    return {
      event_id: String(fields.event_id || newLiveAvatarEventId()),
      event_type: eventType,
      text: String(fields.text || '').trim(),
    };
  }
  return {
    event_id: String(fields.event_id || newLiveAvatarEventId()),
    event_type: eventType,
    ...fields,
  };
}

export async function publishLiveAvatarControl(
  room: Room | null | undefined,
  eventType: LiveAvatarControlEvent,
  fields: Record<string, unknown> = {},
): Promise<void> {
  if (!room || room.state !== 'connected') return;
  const body = buildLiveAvatarControlPayload(eventType, fields);
  const bytes = new TextEncoder().encode(JSON.stringify(body));
  await room.localParticipant.publishData(bytes, {
    reliable: true,
    topic: LIVEAVATAR_CONTROL_TOPIC,
  });
}

/** Verbatim TTS (no extra LLM turn). */
export async function liveAvatarSpeakText(room: Room | null | undefined, text: string) {
  const line = String(text || '').trim();
  if (!line) return;
  if (!room || room.state !== 'connected') {
    throw new Error(`liveavatar_speak_text_room_${room?.state || 'missing'}`);
  }
  await publishLiveAvatarControl(room, 'avatar.speak_text', { text: line });
}

/**
 * Session-limit warn/goodbye: interrupt ongoing avatar speech so the line is audible.
 */
export async function liveAvatarSpeakSessionLimitText(room: Room | null | undefined, text: string) {
  const line = String(text || '').trim();
  if (!line) return;
  if (!room || room.state !== 'connected') {
    throw new Error(`liveavatar_session_limit_speak_room_${room?.state || 'missing'}`);
  }
  await publishLiveAvatarControl(room, 'avatar.interrupt', {});
  await publishLiveAvatarControl(room, 'avatar.speak_text', { text: line });
}
